import React, { useEffect, useState } from 'react';

import {
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  Container,
  useTheme,
  FormControlLabel,
  Switch,
} from '@mui/material';

import { useSetRecoilState } from 'recoil';
import { loadingState, toastState } from '../state/RecoilState';
import validator from 'validator';
import MfsRichTextEditor from '../components/Common/RichText/RichTextEditor';

import RestUtilities from '../services/RestUtilities';
import { BlogPost } from '../@types/api';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';

const BlogContent = () => {
  const { postRowId } = useParams();
  const theme = useTheme();
  const setToast = useSetRecoilState(toastState);
  const setLoading = useSetRecoilState(loadingState);
  const [blogPost, setBlogPost] = useState<BlogPost | undefined>(undefined);
  const [isTouched, setIsTouched] = useState(false);
  const [editor, setEditor] = useState<any>(undefined);
  const [errors, setErrors] = useState<string[]>([]);

  const onUpdate = async () => {
    if (!blogPost || !editor) return;
    const newError: string[] = [];
    if (!blogPost.title) {
      newError.push('title');
    }
    if (!blogPost.slug) {
      newError.push('slug');
    }
    if (!blogPost.description) {
      newError.push('description');
    }
    if (newError.length) {
      setErrors(newError);
      return;
    }
    setLoading(true);
    const newHtml = editor.getHTML();
    const blogUpdate: BlogPost = { ...blogPost, postContent: newHtml };
    if (postRowId) {
      const url = `/api/Blog/PutPost`;
      const response = await RestUtilities.put(url, blogUpdate);
      if (response.ok) {
        setBlogPost(blogUpdate);
        setToast('BlogPost saved');
      }
    } else {
      const url = `/api/Blog/New`;
      const response = await RestUtilities.post(url, blogUpdate);
      if (response.ok) {
        setBlogPost(blogUpdate);
        setToast('BlogPost added');
      }
    }

    setIsTouched(false);
    setLoading(false);
  };

  useEffect(() => {
    const getBlogPost = async () => {
      const res = await RestUtilities.get(`/api/blog/getById/${postRowId}`);
      setBlogPost(res.content);
      setToast('BlogPost loaded');
    };
    if (postRowId) {
      getBlogPost();
    } else {
      const newPost: BlogPost = {
        postRowId: 0,
        postId: '',
        slug: '',
        author: 'Admin',
        postContent: '',
        title: '',
        description: '',
        dateCreated: new Date(),
        dateModified: new Date(),
        isPublished: false,
      };
      setBlogPost(newPost);
    }
  }, [postRowId, setToast]);

  const onTitleChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const text = validator.escape(e.currentTarget.value);
    const updated: BlogPost = { ...(blogPost as BlogPost), title: text };
    setBlogPost(updated);
    setIsTouched(true);
    if (text && errors.includes('title')) {
      setErrors(errors.filter((x) => x !== 'title'));
    }
  };

  const onSlugChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const text = e.currentTarget.value.toLowerCase().replace(' ', '-');
    const updated: BlogPost = { ...(blogPost as BlogPost), slug: text };
    setBlogPost(updated);
    setIsTouched(true);
    if (text && errors.includes('slug')) {
      setErrors(errors.filter((x) => x !== 'slug'));
    }
  };

  const onDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const text = e.currentTarget.value;
    const updated: BlogPost = { ...(blogPost as BlogPost), description: text };
    setBlogPost(updated);
    setIsTouched(true);
    if (text && errors.includes('description')) {
      setErrors(errors.filter((x) => x !== 'description'));
    }
  };

  const onPublishChange = () => {
    if (!blogPost) return;
    const updated: BlogPost = {
      ...(blogPost as BlogPost),
      isPublished: !blogPost.isPublished,
    };
    setBlogPost(updated);
    setIsTouched(true);
  };

  if (!blogPost) return null;

  const titleTest = postRowId ? `Editing: ${blogPost.postRowId}` : 'New Post';
  return (
    <Container maxWidth='lg'>
      <Box display='flex' alignItems='center'>
        <Typography variant='h4'>{titleTest}</Typography>
        <Button
          variant='contained'
          color='primary'
          onClick={onUpdate}
          size='small'
          disabled={!isTouched}
          sx={{ ml: 2 }}
        >
          Save Changes
        </Button>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box display={'flex'} alignItems={'center'}>
            <FormControlLabel
              control={
                <Switch
                  checked={blogPost.isPublished}
                  onChange={onPublishChange}
                />
              }
              label='Published'
            />
          </Box>
        </Grid>
        <Grid item xs={6}>
          <TextField
            id='title'
            label='Title'
            required
            error={errors.includes('title')}
            helperText='Title is required'
            value={blogPost.title ?? ''}
            fullWidth
            margin='normal'
            onChange={onTitleChange}
            variant='outlined'
            size='small'
            sx={{ my: 1 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            id='slug'
            label='Slug'
            required
            error={errors.includes('slug')}
            helperText='Slug is required'
            value={blogPost.slug ?? ''}
            fullWidth
            margin='normal'
            onChange={onSlugChange}
            variant='outlined'
            size='small'
            sx={{ my: 1 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            id='summary'
            label='Summary'
            required
            error={errors.includes('description')}
            helperText='Summary is required'
            multiline
            value={blogPost.description ?? ''}
            fullWidth
            margin='normal'
            onChange={onDescriptionChange}
            variant='outlined'
            size='small'
            sx={{ my: 1 }}
            InputProps={{
              rows: 2,
              multiline: true,
              inputComponent: 'textarea',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant='subtitle1' color={theme.palette.text.secondary}>
            Created: {dayjs(blogPost.dateCreated).format('ddd-MM-yyyy')},
            LastUpdated: {dayjs(blogPost.dateModified).format('ddd-MM-yyyy')},
            Author: {blogPost.author}
          </Typography>
          <Box sx={{ my: 1, pb: 3 }}>
            <MfsRichTextEditor
              content={blogPost.postContent ?? ''}
              setEditor={(editor) => setEditor(editor)}
              setTouched={() => setIsTouched(true)}
              isSatellite={false}
              isBlog
            />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default BlogContent;
